import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import Select from "react-select";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import { Icon } from "../../../components/Component";
import { servicePutLivechatResolve } from "../../../services/livechat";
import { serviceGetTransactions, serviceResolveTransaction } from "../../../services/transactions";
import { formatTransactionOptionLabel, toResolveProducts } from "../../../utils/formatTransaction";

const ERROR_TEXT = {
  "error.livechat.resolve_forbidden": "Hanya Sales atau CS pemegang chat yang bisa resolve.",
  "error.livechat.ticket_resolved": "Tiket ini sudah RESOLVED.",
  "error.livechat.no_ticket": "Percakapan ini belum punya tiket.",
};

export const isPhoneValid = (value) => /^\+?[0-9]{8,16}$/.test(value?.trim() ?? "");
export const isEmailValid = (value) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value?.trim() ?? "");

const isOk = (res) => res?.status === 200 || res?.status === 201;

// Resolve with a sale, the transaction flow of the WhatsApp resolve modal.
const LivechatModalResolve = ({ isOpen, conversation, contact, ticket, onClose, onResolved }) => {
  const [cookies] = useCookies(["SIDikey", "pcData"]);
  const phone = contact?.phones?.[0]?.phone ?? "";
  const email = contact?.emails?.[0]?.email ?? "";

  const [lookupType, setLookupType] = useState("phone");
  const [lookupValue, setLookupValue] = useState(phone);
  const [txList, setTxList] = useState([]);
  const [txChecked, setTxChecked] = useState(false);
  const [txError, setTxError] = useState("");
  const [selected, setSelected] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetLookup = () => {
    setTxList([]);
    setTxChecked(false);
    setTxError("");
    setSelected(null);
  };

  useEffect(() => {
    if (!isOpen) return;

    setLookupType("phone");
    setLookupValue(phone);
    resetLookup();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLookupValid = lookupType === "phone" ? isPhoneValid(lookupValue) : isEmailValid(lookupValue);

  const changeLookupType = (type) => {
    setLookupType(type);
    setLookupValue(type === "phone" ? phone : email);
    resetLookup();
  };

  const checkTransactions = async () => {
    if (!isLookupValid) return;

    setIsChecking(true);
    resetLookup();
    const value = lookupValue.trim();
    const res = await serviceGetTransactions({
      startTransactionDate: ticket?.createdAt,
      ...(lookupType === "phone" ? { clientPhone: value } : { clientEmail: value }),
    });
    setIsChecking(false);
    setTxChecked(true);

    if (res?.status !== 200) {
      setTxError(res?.data?.message || "Gagal menghubungi service transaksi.");
      return;
    }

    const items = res.data?.data ?? [];
    setTxList(items);
    if (items.length === 0) {
      setTxError(`Tidak ada transaksi ditemukan untuk ${lookupType === "phone" ? "nomor telepon" : "email"} ini.`);
    }
  };

  const submit = async () => {
    if (!selected || !ticket?.id) return;

    setIsSubmitting(true);
    const tx = selected.item;
    const resolved = await serviceResolveTransaction({
      crmTicketId: ticket.id,
      orderId: tx.order_id,
      pc: tx.profit_center,
    });

    if (!isOk(resolved)) {
      setIsSubmitting(false);
      Swal.fire("Not Found", "Transaksi tidak ditemukan di database Astronacci.", "error");
      return;
    }

    const res = await servicePutLivechatResolve(cookies.SIDikey, conversation.conversationId, {
      pc: (cookies.pcData ?? []).find((item) => item?.deptId === conversation.departmentId)?.pc ?? 0,
      phone: tx.client_phone || phone || undefined,
      products: toResolveProducts(tx),
    });
    setIsSubmitting(false);

    if (res.status === 200) {
      Swal.fire("Submited", "resolve", "success");
      onResolved(res.data);
      return;
    }

    Swal.fire("Gagal resolve", ERROR_TEXT[res.data?.message] ?? "Coba lagi.", "error");
  };

  const options = txList.map((tx) => ({
    value: tx.order_id || tx.transaction_id || tx.id,
    label: formatTransactionOptionLabel(tx),
    item: tx,
  }));

  return (
    <Modal isOpen={isOpen} backdrop="static" toggle={onClose} className="modal-dialog-centered" size="lg">
      <ModalBody>
        <button type="button" className="close btn btn-icon btn-trigger" aria-label="Tutup" onClick={onClose}>
          <Icon name="cross-sm" />
        </button>
        <div className="text-center">
          <span className="modal-resolve-title">Status: </span>
          <span className={`modal-resolve-title color-${conversation.status}`}>{conversation.status}</span>
          <div>
            <i className="bi bi-box-arrow-in-up modal-resolve-icon blue" />
          </div>
        </div>

        <div className="card card-bordered p-3 mb-3 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="font-weight-bold text-dark" style={{ fontSize: "13px" }}>
              <Icon name="search" className="mr-1 text-primary" /> Check Transactions
            </span>
            <div className="btn-group btn-group-sm" role="group" aria-label="Cari berdasarkan">
              {[
                ["phone", "No. Telepon"],
                ["email", "Email"],
              ].map(([type, label]) => (
                <button
                  key={type}
                  type="button"
                  className={`btn btn-xs ${lookupType === type ? "btn-primary" : "btn-outline-light text-dark"}`}
                  aria-pressed={lookupType === type}
                  onClick={() => changeLookupType(type)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="input-group">
            <input
              type={lookupType === "email" ? "email" : "tel"}
              inputMode={lookupType === "email" ? "email" : "numeric"}
              className={`form-control ${lookupValue && !isLookupValid ? "border-danger" : ""}`}
              placeholder={lookupType === "phone" ? "Masukkan nomor telepon client..." : "Masukkan email client..."}
              aria-label={lookupType === "phone" ? "Nomor telepon client" : "Email client"}
              value={lookupValue}
              onChange={(e) => {
                const value =
                  lookupType === "phone"
                    ? e.target.value.replace(/[^0-9+]/g, "").replace(/(?!^)\+/g, "")
                    : e.target.value;
                setLookupValue(value);
                if (txChecked) resetLookup();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  checkTransactions();
                }
              }}
            />
            <div className="input-group-append">
              <Button color="primary" disabled={isChecking || !isLookupValid} onClick={checkTransactions}>
                {isChecking ? (
                  <>
                    <Spinner size="sm" className="mr-1" /> Mengecek...
                  </>
                ) : (
                  <>
                    <Icon name="search" className="mr-1" /> Check Transactions
                  </>
                )}
              </Button>
            </div>
          </div>

          {lookupValue && !isLookupValid && (
            <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
              <Icon name="alert-circle" className="mr-1" />
              {lookupType === "phone"
                ? "Format nomor telepon tidak valid (masukkan 8-16 digit angka)."
                : "Format email tidak valid (contoh: user@domain.com)."}
            </div>
          )}

          {txChecked && txList.length === 0 && (
            <div className="alert alert-warning py-2 mb-0 mt-2" style={{ fontSize: "13px" }}>
              <Icon name="alert-circle" className="mr-1" />
              {txError}
            </div>
          )}
        </div>

        {txList.length > 0 && (
          <div className="card card-bordered p-3 mb-3 bg-white shadow-sm border-primary">
            <h6 className="font-weight-bold mb-2 text-primary" style={{ fontSize: "13px" }}>
              <Icon name="check-circle" className="mr-1 text-success" /> Pilih Produk yang Diselesaikan
            </h6>
            <Select
              value={selected}
              onChange={setSelected}
              placeholder="Pilih produk yang diselesaikan..."
              aria-label="Pilih transaksi"
              options={options}
            />
            <Button
              color="primary"
              className="mt-3 mx-auto"
              block
              style={{ justifyContent: "center" }}
              disabled={!selected || isSubmitting}
              onClick={submit}
            >
              {isSubmitting ? <Spinner size="sm" /> : "Submit Resolve"}
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default LivechatModalResolve;
